<template lang="pug">
section#dashboard
  section.section-gap.achivement-area
    .container
      .row
        .col
          .banner-content(v-show="!loading")
            h1 {{ $t('dashboard.title')}}
            p(v-html="$t('dashboard.subtitle')")
            a.clear-data(v-show="!loading", href="/", v-on:click.prevent="clearDB")
              i.fa.fa-fw.fa-trash
              | Clear data
          full-loader(v-show="loading")
          .glucoseInfos
            p(v-if="rangeFocused").range For 
              b {{ rangeFocused }}
              | :
            p(v-if="timeInRange").time
              span.title In Range
              span ~{{ timeInRange.toFixed(1) }}%
            p(v-if="average").average
              span.title Average
              span {{ average.toFixed(0) }}&nbsp;mg/dL
            p(v-if="hba").hba
              span.title Hba1C
              span {{ hba.toFixed(2) }}%
          #chart.chart(v-show="!loading")
          p.download
            a.download-data#download-data.genric-btn.circle.link(v-if="!loading", v-on:click="downloadData")
              span.lnr.lnr-cloud-download
              | &nbsp;{{ $t('dashboard.download') }}
      .row.controls(v-show="!loading")
        .col-md-6.events
          h2 {{ $t('dashboard.events.title')}}
          p.empty(v-show="!visibleEventsNumber") {{ $t('dashboard.events.nothing') }}
          ul(v-bind:class="{hasfocus: focusedEvent}")
            li.event-entry(v-for="ev in events", v-on:click="showEvent(ev)", v-bind:class="{focused: ev.focused, display: ev.display}")
              span.color-bubble(v-bind:style="{'background': ev.color && ev.color.background}")
              span.event-title(href="#") {{ ev.summary }}
              span(v-show="ev.location", v-bind:title="ev.location").loc.clearfix {{ ev.location | crop(47, '...') }}
        .col-md-6.data
          h2 {{ $t('dashboard.data.title')}}
          div(v-if="focusedEvent")
            p.title {{ focusedEvent.summary }} - {{ moment(focusedEvent.start.dateTime || focusedEvent.start.date).format('DD/MM/YYYY') }}
          div(v-show="moreLoading")
            full-loader
          div(v-show="!moreLoading")
            p.empty(v-show="!focusedEvent") {{ $t('dashboard.data.nothing') }}
            div(v-if="focusedPosition")
              h4 {{ $t('dashboard.data.position.title') }}
              p(v-if="focusedEvent.location").address {{ focusedEvent.location }}
              p.address {{ focusedPosition.address.city }}, {{ focusedPosition.address.state }} - {{ focusedPosition.address.country }}
            div(v-if="focusedMeal")
              h4 {{ $t('dashboard.data.meal.title') }}
              p.calories
                span {{ focusedMeal.calories }}
                | &nbsp;calories -&nbsp;
                a.details(data-toggle="collapse", href="#mealDetails")
                  | More details
              #mealDetails.collapse
                .meals(v-for="(meal, mealName) in focusedMeal.meals")
                  h6.meal-name {{ mealName | capitalize }}
                  ul.parts-list
                    li(v-for="part in meal.parts", v-bind:title="part") {{ part | crop(30, '...') }}
            div(v-if="focusedMeteo")
              h4 {{ $t('dashboard.data.meteo.title') }}
              p.temp
                span.t {{ focusedMeteo.data[0].temp }}°C
                | ({{ focusedMeteo.data[0].min_temp }}°C - {{ focusedMeteo.data[0].max_temp}}°C)
                br
                span.rain(v-show="focusedMeteo.data[0].precip >= 1")
                  i.fa.fa-fw.fa-tint
                  | &nbsp; {{ focusedMeteo.data[0].precip }}mm
            div(v-if="focusedPollution")
              h4 {{ $t('dashboard.data.pollution.title') }}
              p.pollution.risk(v-bind:class="'risk-' + focusedPollution.risk")
                span {{ focusedPollution.text }}
                | &nbsp;({{ focusedPollution.type }}: {{ focusedPollution.value }}{{ focusedPollution.unit }})

</template>

<script>
import moment from 'moment';
import Highcharts from 'highcharts/highstock';
import addMore from 'highcharts/highcharts-more';
import addExporting from 'highcharts/modules/exporting';
import FileSaver from 'file-saver';
import swal from 'sweetalert2';

import api from '@/api';
import FSLParser from '@/assets/js/parseFSL';
import FullLoader from '@/components/partials/FullLoader';

const HIPO_LIMIT = 70;
const HIPER_LIMIT = 180;

export default {
  name: 'Dashboard',
  components: {
    'full-loader': FullLoader,
  },
  data() {
    return {
      chart: null,
      loading: true,
      events: [],
      focusedEvent: null,
      focusedMeal: null,
      focusedMeteo: null,
      focusedPollution: null,
      focusedPosition: null,
      moreLoading: false,
      hba: null,
      average: null,
      timeInRange: null,
      rangeFocused: null,
      moment,
    };
  },
  computed: {
    visibleEventsNumber() {
      return this.events.filter(ev => ev.display).length;
    },
    hasData() {
      return this.$store.dbLoaded && !!this.$store.sugarData.getCollection('entries').find().length;
    },
  },
  metaInfo() {
    return {
      title: this.$t('dashboard.meta.title'),
    };
  },
  methods: {
    clearDB() {
      swal({
        title: 'Are you sure?',
        text: 'Do you really want to delete all loaded data?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Delete',
      }).then((result) => {
        if (result.value) {
          swal(
            'Deleted!',
            'Your data has been cleared.',
            'success',
          );
          this.$store.sugarData.getCollection('entries').clear();
          this.$router.push('/');
        }
      });
    },
    downloadData() {
      const db = this.$store.sugarData.serialize();
      const date = moment().format('YYYY-MM-DD');
      const file = new File([db], `sugarnanny-dump-${date}.json`, { type: 'text/plain;charset=utf-8' });
      FileSaver.saveAs(file);
    },
    extractGlycaemias(from, to) {
      this.rangeFocused = moment.duration(moment(from).diff(moment(to))).humanize();
      const entries = this.$store.sugarData.getCollection('entries')
        .chain()
        .where((entry) => {
          if (entry.RecordType === '1' || entry.RecordType === '0') {
            const d = new Date(entry.Time);
            return d >= from && d <= to;
          }
          return false;
        }).data();
      const glycaemias = entries.map((e) => {
        switch (e.RecordType) {
          case '0':
            return parseInt(e.HistoricGlucose, 10);
          case '1':
            return parseInt(e.ScanGlucose, 10);
          default:
            break;
        }
        return null;
      });
      return glycaemias;
    },
    calculateTimeInRange(from, to) {
      const glycaemias = this.extractGlycaemias(from, to);
      if (!glycaemias.length) return null;
      let under = 0;
      let over = 0;
      glycaemias.forEach((g) => {
        if (g < HIPO_LIMIT) under += 1;
        if (g > HIPER_LIMIT) over += 1;
      });
      return ((glycaemias.length - under - over) / glycaemias.length) * 100;
    },
    calculateAverage(from, to) {
      const glycaemias = this.extractGlycaemias(from, to);
      if (!glycaemias.length) return null;
      return glycaemias.reduce((total, score) => total + score) / glycaemias.length;
    },
    calculateHbac(from, to) {
      if (!this.average) this.average = this.calculateAverage(from, to);
      if (!this.average) return null;
      return (46.7 + this.average) / 28.7;
    },
    startAll() {
      if (!this.$store.dbLoaded) {
        setTimeout(() => {
          this.startAll();
        }, 300);
      } else if (this.hasData) {
        this.events = this.$store.sugarData.getCollection('events').find().reverse();
        this.events.forEach((ev) => { ev.display = true; });

        const report = FSLParser.parseReport(this.$store.sugarData.getCollection('entries').find());
        const highchartsData = [
          {
            name: 'Glucose',
            color: '#265776',
            data: [],
            connectNulls: false,
            zones: [],
          },
          {
            name: 'Rapid-Acting Insulin',
            color: '#dc6d5f',
            connectNulls: false,
            data: [],
          },
          {
            name: 'Long-Acting Insulin',
            color: '#aeaeae',
            connectNulls: false,
            data: [],
          },
          {
            name: 'Food',
            color: '#265776',
            data: [],
            lineWidth: 0,
            marker: {
              enabled: true,
              radius: 4,
            },
          },
          {
            name: 'Rapid-Acting Insulin Injection',
            color: '#dc6d5f',
            data: [],
            lineWidth: 0,
            showInLegend: false,
            marker: {
              enabled: true,
              radius: 4,
            },
          },
          {
            name: 'Long-Acting Insulin Injection',
            color: '#aeaeae',
            data: [],
            lineWidth: 0,
            showInLegend: false,
            marker: {
              enabled: true,
              radius: 4,
            },
          },
          {
            name: 'Hypo',
            color: '#cdcdcd',
            dashStyle: 'Dash',
            lineWidth: 1.5,
            showInLegend: false,
            data: [],
          },
          {
            name: 'Hyper',
            color: '#cdcdcd',
            lineWidth: 1.5,
            showInLegend: false,
            dashStyle: 'Dash',
            data: [],
          },
        ];

        let lastDate = null;
        const breaks = [];
        report.glucose.forEach((element) => {
          const value = element.glucose.getValueAsMgPerDl();
          if (lastDate === null) lastDate = moment(element.date);
          if (lastDate.diff(moment(element.date), 'days') !== 0) {
            breaks.push({
              from: lastDate.valueOf(),
              to: element.date.valueOf(),
              breakSize: Math.abs(lastDate.diff(moment(element.date))),
            });
          }
          lastDate = moment(element.date);
          highchartsData[0].data.push([element.date.valueOf(), value]);
        });
        breaks.forEach((br) => {
          const lines = [
            highchartsData[0],
            highchartsData[1],
            highchartsData[2],
          ];
          lines.forEach((l) => {
            if (!l.zones) l.zones = [];
            l.zoneAxis = 'x';
            l.zones.push({
              color: l.color,
              value: br.from,
            });
            l.zones.push({
              color: 'rgba(0, 0, 0, 0)',
              value: br.to,
            });
          });
        });
        report.getSmoothRapidInsulin().forEach((element) => {
          const value = element.units * 18;
          highchartsData[1].data.push([element.date.valueOf(), value]);
        });
        report.getSmoothLongInsulin().forEach((element) => {
          const value = element.units * 18;
          highchartsData[2].data.push([element.date.valueOf(), value]);
        });
        report.food.forEach((element) => {
          const value = element.carbs;
          highchartsData[3].data.push([element.date.valueOf(), (isNaN(value) ? 5 : value)]);
        });
        report.insulin.forEach((element) => {
          const value = element.units;
          if (element.insulinType === 0) {
            highchartsData[4].data.push([element.date.valueOf(), value]);
          }
          if (element.insulinType === 1) {
            highchartsData[5].data.push([element.date.valueOf(), value]);
          }
        });
        highchartsData[6].data = [
          [report.start.valueOf(), HIPO_LIMIT],
          [report.end.valueOf(), HIPO_LIMIT],
        ];
        highchartsData[7].data = [
          [report.start.valueOf(), HIPER_LIMIT],
          [report.end.valueOf(), HIPER_LIMIT],
        ];

        this.chart = Highcharts.stockChart('chart', {
          chart: {
            zoomType: 'x',
            events: {
              load: () => {
                this.loading = false;
              },
            },
          },
          plotOptions: {
            series: {
              connectNulls: false,
            },
          },
          time: {
            useUTC: false,
          },
          tooltip: {
            enabled: false,
          },
          series: highchartsData,
          xAxis: {
            breaks,
            events: {
              setExtremes: (ev) => {
                const minDateAxis = new Date(ev.min);
                const maxDateAxis = new Date(ev.max);
                this.average = this.calculateAverage(minDateAxis, maxDateAxis);
                this.hba = this.calculateHbac(minDateAxis, maxDateAxis);
                this.timeInRange = this.calculateTimeInRange(minDateAxis, maxDateAxis);
                this.events.forEach((event) => {
                  const start = new Date(event.start.dateTime || event.start.date);
                  const end = new Date(event.end.dateTime || event.end.date);
                  if (minDateAxis <= start && end <= maxDateAxis) {
                    event.display = true;
                  } else {
                    event.display = false;
                  }
                });
                const events = this.events;
                this.events = [];
                this.events = events;
              },
            },
          },
          yAxis: {
            plotBands: [{
              from: HIPO_LIMIT,
              to: HIPER_LIMIT,
              color: 'rgba(140, 140, 140, 0.1)',
            }],
          },
          rangeSelector: {
            selected: 1,
            buttons: [
              {
                type: 'day',
                count: 1,
                text: '1d',
              },
              {
                type: 'day',
                count: 3,
                text: '3d',
              },
              {
                type: 'day',
                count: 7,
                text: '1w',
              },
              {
                type: 'month',
                count: 1,
                text: '1m',
              },
              {
                type: 'month',
                count: 3,
                text: '3m',
              },
              {
                type: 'all',
                text: 'All',
              },
            ],
          },
          legend: {
            enabled: true,
            layout: 'horizontal',
            backgroundColor: '#FFFFFF',
            align: 'right',
            x: -30,
            y: 50,
            verticalAlign: 'top',
            floating: true,
            shadow: true,
          },
        });

        const minDateAxis = new Date(this.chart.xAxis[0].getExtremes().min);
        const maxDateAxis = new Date(this.chart.xAxis[0].getExtremes().max);
        this.events.forEach((event) => {
          const start = new Date(event.start.dateTime || event.start.date);
          const end = new Date(event.end.dateTime || event.end.date);
          if (minDateAxis <= start && end <= maxDateAxis) {
            event.display = true;
          } else {
            event.display = false;
          }
        });
        const events = this.events;
        this.events = [];
        this.events = events;
        this.events.forEach((event) => {
          let d = new Date(event.start.dateTime || event.start.date);
          this.chart.xAxis[0].addPlotLine({
            color: event.color ? event.color.background : 'rgba(30, 30, 30, 0.3)',
            label: {
              text: `${event.summary.substr(0, 10)}...`,
              align: 'left',
              x: 10,
              y: 15,
              rotation: 0,
              style: {
                color: event.color ? event.color.background : 'rgba(30, 30, 30, 0.3)',
              },
            },
            value: +d,
            width: 1,
            id: `${event.id}-0`,
          });
          d = new Date(event.end.dateTime || event.end.date);
          this.chart.xAxis[0].addPlotLine({
            color: event.color ? event.color.background : 'rgba(30, 30, 30, 0.3)',
            value: +d,
            width: 1,
            id: `${event.id}-1`,
          });
        });
      } else {
        this.$router.push('/start');
      }
    },
    showEvent(event) {
      document.querySelector('#chart').scrollIntoView({
        behavior: 'smooth',
      });
      if (this.focusedEvent) this.focusedEvent.focused = false;
      event.focused = true;
      this.focusedEvent = event;
      this.moreLoading = true;
      let start = null;
      let originalStart = null;
      if (event.start.date) {
        originalStart = new Date(event.start.date);
        start = new Date(originalStart.getTime());
      } else if (event.start.dateTime) {
        originalStart = new Date(event.start.dateTime);
        start = new Date(originalStart.getTime());
        start.setHours(start.getHours() - 6);
      }
      const pos = this.$store.sugarData.getCollection('locations')
        .chain()
        .where((obj) => {
          const d = new Date(obj.date);
          return d <= originalStart;
        })
        .simplesort('date', true)
        .limit(1)
        .data()[0];

      this.focusedMeal = this.$store.sugarData.getCollection('meals')
        .chain()
        .where((obj) => {
          const d = new Date(obj.date);
          return d <= originalStart;
        })
        .simplesort('date', true)
        .limit(1)
        .data();
      this.focusedMeal = this.focusedMeal.length ? this.focusedMeal[0] : null;

      const apiDateStart = moment(originalStart).format('YYYY-MM-DD');
      const calls = [];
      calls.push(api.get(`/location/${pos.lat};${pos.lng}`)
        .then((res) => {
          this.focusedPosition = res.data;
        })
        .catch((err) => {
          console.log(err);
        }));
      calls.push(api.get(`/weather/${pos.lat};${pos.lng}?from=${apiDateStart}&to=${apiDateStart}`)
        .then((res) => {
          this.focusedMeteo = res.data;
        })
        .catch((err) => {
          console.log(err);
        }));
      calls.push(api.get(`/pollution/${pos.lat};${pos.lng}?from=${apiDateStart}&to=${apiDateStart}`)
        .then((res) => {
          const obj = {};
          res.data.results.forEach((result) => {
            if (obj.type === 'pm25') return;
            if (result.parameter === 'pm25' || result.parameter === 'pm10') {
              obj.type = result.parameter;
              obj.value = result.value;
              obj.unit = result.unit;
              if (obj.value <= 12.0) obj.risk = 0;
              else if (obj.value <= 35.4) obj.risk = 1;
              else if (obj.value <= 55.4) obj.risk = 2;
              else if (obj.value <= 150.4) obj.risk = 3;
              else obj.risk = 5;

              switch (obj.risk) {
                case 0:
                  obj.text = 'Very good';
                  break;
                case 1:
                  obj.text = 'Moderate';
                  break;
                case 2:
                  obj.text = 'Somewhat unhealthy';
                  break;
                case 3:
                  obj.text = 'Unhealthy';
                  break;
                case 4:
                  obj.text = 'Very unhealthy';
                  break;
                case 5:
                  obj.text = 'Hazardous';
                  break;
                default:
                  break;
              }
            }
          });
          if (obj.value) this.focusedPollution = obj;
        })
        .catch((err) => {
          console.log(err);
        }));
      Promise.all(calls)
        .then(() => {
          this.moreLoading = false;
        })
        .catch((err) => {
          console.log(err);
          this.moreLoading = false;
        });

      let end = null;
      let originalEnd = null;
      if (event.end.date) {
        originalEnd = new Date(event.end.date);
        end = new Date(originalEnd.getTime());
      } else if (event.end.dateTime) {
        originalEnd = new Date(event.end.dateTime);
        end = new Date(originalEnd.getTime());
        end.setHours(end.getHours() + 6);
      }


      this.chart.xAxis[0].removePlotBand('event');
      this.chart.xAxis[0].addPlotBand({
        color: 'rgba(30, 30, 30, .15)',
        // from: +(new Date(originalStart.getTime() - (originalStart.getTimezoneOffset() * 60000))),
        from: +originalStart,
        // to: +(new Date(originalEnd.getTime() - (originalEnd.getTimezoneOffset() * 60000))),
        to: +originalEnd,
        id: 'event',
      });
      this.chart.xAxis[0].setExtremes(+start, +end);
    },
  },
  mounted() {
    // localStorage.clear();
    setTimeout(() => {
      addMore(Highcharts);
      addExporting(Highcharts);
      this.startAll();
    }, 300);
  },
};
</script>

<style lang="stylus" scoped>
@import '../../assets/css/variables.styl'

.banner-area
  padding-bottom base-padding

#chart
  border 1px solid color-subtle
  // overflow hidden
  padding 15px
  box-shadow 0px 0px 14px rgba(0, 0, 0, .15)

p.empty
  margin 20px 0

.glucoseInfos
  width 100%
  position absolute
  right 0
  padding 15px
  padding-top 0
  margin-top -55px
  text-align right

p.hba
p.average
p.time
p.range
  display inline
  text-align right
  font-size 1.2em
  margin-left 5px
  //border 1px solid red
  position relative
  overflow hidden
  span.title
    position absolute
    font-variant small-caps
    text-transform lowercase
    display inline
    bottom -25px
    text-align center
    //border 1px solid red
    width 100%
    padding-left 4px
  span:not(.title)
    font-size 1em
    margin-left 5px
    font-weight bold
    background color-main
    color white
    padding 6px
    border-radius 8px

p.range
  font-size 0.9em

.clear-data
  position absolute
  top 25px
  right 10px
  transition ease .2s all
  opacity .4
  font-size 1.25em
  font-variant small-caps
  text-transform uppercase
  font-weight 400
  &:hover
    opacity 1

.controls
  margin-top 40px
  padding-top 25px
  //border-top 1px solid rgba(0, 0, 0, .15)

  p.pollution
    margin-top 8px

  p.temp
    font-style italic
    span.rain
      color color-main
      font-style normal
    span.t
      font-style normal
      font-weight bold
      padding-right 5px
  p.risk
    span
      color white
      padding 4px
      border-radius 6px
  p.risk-0
    span
      background #7aaf64
  p.risk-5
    span
      background #000000
  p.risk-4
    span
      background #A10649
  p.risk-3
    span
      background #ad4030
  p.risk-1
    span
      background #d8db4a
  p.risk-2
    span
      background #EB8A14

  p.title
    font-size 1em
    font-variant small-caps
    text-transform uppercase
    font-weight 500
    opacity .7
  p.calories
    span
      font-weight bold
  .meals
    ul
      margin 0
    h6
      margin-top 10px

  ul
    margin-top 10px
    max-width 350px
    &.hasfocus
      .event-entry:not(.focused)
        opacity .4
    .event-entry
      cursor pointer
      padding 10px
      border 1px solid rgba(0, 0, 0, .15)
      margin-bottom 8px
      box-shadow 0 0 4px rgba(0, 0, 0, .15)
      transition all ease .2s
      &:not(.display)
        visibility hidden
        display none
      &.focused
        cursor default
      &:hover, &.focused
        box-shadow none
        .color-bubble
          background white!important
    .event-title
      margin-left -10px
      font-weight 400
    .color-bubble
      background white
      transition background ease .2s
      height 15px
      width 15px
      border 1px solid rgba(0, 0, 0, .15)
      display inline-block
      position relative
      left 100%
      margin-left 3px
      top 3px
      border-radius 50%
    .loc
      font-size .8em
      display inline-block
      clear both
      padding-left 9px

p.download
  margin-top 15px
  margin-bottom 0
</style>



// WEBPACK FOOTER //
// src/components/pages/Dashboard.vue